import { useCallback, useMemo, useState } from "react";
import { PageSearchCondition } from "../../../types/page";
import { ListQueryType } from "../../../types/list";
import { FilterType, FilterValue } from "../../../types/filter";
import { Gender } from "../../../types/common";
import { DataSearchTestType } from "../../../types/dataSearch";
import { useFilterValues } from "../../../hooks/list/useFilterValues";
import { useListSearchParams } from "../../../hooks/list/useListSearchParams";

export interface DataSearchListQueryType extends ListQueryType {
  q?: string;
  ageYear?: string;
  gender?: string;
  state?: string;
  city?: string;
  endedAtFrom?: string;
  endedAtTo?: string;
  testType?: string;
  onlyCompleted?: string;
  excludeTestAccount?: string;
}

export interface DataSearchFilterValues {
  qFilterValue: FilterValue<string>;
  ageYearFilterValue: FilterValue<string>;
  genderFilterValue: FilterValue<Gender | "">;
  stateFilterValue: FilterValue<string>;
  cityFilterValue: FilterValue<string>;
  endedAtFromFilterValue: FilterValue<Date | undefined>;
  endedAtToFilterValue: FilterValue<Date | undefined>;
  testTypeFilterValue: FilterValue<DataSearchTestType | "">;
  onlyCompletedFilterValue: FilterValue<boolean>;
  excludeTestAccountFilterValue: FilterValue<boolean>;
}

export function useDataSearchFilterValues() {
  const [q, setQ] = useState("");
  const [ageYear, setAgeYear] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [endedAtFrom, setEndedAtFrom] = useState<Date | undefined>();
  const [endedAtTo, setEndedAtTo] = useState<Date | undefined>();
  const [testType, setTestType] = useState<DataSearchTestType | "">("");
  const [onlyCompleted, setOnlyCompleted] = useState(false);
  const [excludeTestAccount, setExcludeTestAccount] = useState(false);

  const [pageInfoOnFilter, setPageInfoOnFilter] = useState<PageSearchCondition>({});

  const { listQueryType, setUrlSearchParams } = useListSearchParams<DataSearchListQueryType>();

  const filterValueArray: FilterValue<any>[] = useMemo(
    () => [
      { key: "q", value: q, setter: setQ, defaultValue: "", type: FilterType.STRING },
      { key: "ageYear", value: ageYear, setter: setAgeYear, defaultValue: "", type: FilterType.STRING },
      { key: "gender", value: gender, setter: setGender, defaultValue: "", type: FilterType.OTHER },
      { key: "state", value: state, setter: setState, defaultValue: "", type: FilterType.STRING },
      { key: "city", value: city, setter: setCity, defaultValue: "", type: FilterType.STRING },
      {
        key: "endedAtFrom",
        value: endedAtFrom,
        setter: setEndedAtFrom,
        defaultValue: undefined,
        type: FilterType.DATE,
      },
      {
        key: "endedAtTo",
        value: endedAtTo,
        setter: setEndedAtTo,
        defaultValue: undefined,
        type: FilterType.DATE,
      },
      { key: "testType", value: testType, setter: setTestType, defaultValue: "", type: FilterType.OTHER },
      {
        key: "onlyCompleted",
        value: onlyCompleted,
        setter: setOnlyCompleted,
        defaultValue: false,
        type: FilterType.BOOLEAN,
      },
      {
        key: "excludeTestAccount",
        value: excludeTestAccount,
        setter: setExcludeTestAccount,
        defaultValue: false,
        type: FilterType.BOOLEAN,
      },
    ],
    [q, ageYear, gender, state, city, endedAtFrom, endedAtTo, testType, onlyCompleted, excludeTestAccount]
  );

  const { getFilterValueByKey } = useFilterValues({
    filterValueArray,
    listQueryType,
    setPageInfoOnFilter,
  });

  const filterValues = useMemo(
    () =>
      ({
        qFilterValue: getFilterValueByKey("q") as FilterValue<string>,
        ageYearFilterValue: getFilterValueByKey("ageYear") as FilterValue<string>,
        genderFilterValue: getFilterValueByKey("gender") as FilterValue<Gender | "">,
        stateFilterValue: getFilterValueByKey("state") as FilterValue<string>,
        cityFilterValue: getFilterValueByKey("city") as FilterValue<string>,
        endedAtFromFilterValue: getFilterValueByKey("endedAtFrom") as FilterValue<Date | undefined>,
        endedAtToFilterValue: getFilterValueByKey("endedAtTo") as FilterValue<Date | undefined>,
        testTypeFilterValue: getFilterValueByKey("testType") as FilterValue<DataSearchTestType | "">,
        onlyCompletedFilterValue: getFilterValueByKey("onlyCompleted") as FilterValue<boolean>,
        excludeTestAccountFilterValue: getFilterValueByKey("excludeTestAccount") as FilterValue<boolean>,
      } as DataSearchFilterValues),
    [getFilterValueByKey]
  );

  const searchList = useCallback(
    (pageSearchCondition: PageSearchCondition, listQueryTypeParam?: DataSearchListQueryType) => {
      const param: DataSearchListQueryType = {
        q: listQueryTypeParam?.q,
        ageYear: listQueryTypeParam?.ageYear,
        gender: listQueryTypeParam?.gender,
        state: listQueryTypeParam?.state,
        city: listQueryTypeParam?.city,
        endedAtFrom: listQueryTypeParam?.endedAtFrom,
        endedAtTo: listQueryTypeParam?.endedAtTo,
        testType: listQueryTypeParam?.testType,
        onlyCompleted: listQueryTypeParam?.onlyCompleted,
        excludeTestAccount: listQueryTypeParam?.excludeTestAccount,
      };

      return () => setUrlSearchParams(param, pageSearchCondition);
    },
    [setUrlSearchParams]
  );

  return {
    pageInfoOnFilter,
    filterValues,
    searchList,
    listQueryType,
  };
}
